import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { publicApi, Pattern, Style, SkillLevel, Instructor } from '../services/api';
import VideoThumbnail from '../components/VideoThumbnail';
import { SkeletonGrid } from '../components/Skeleton';
import '../components/PlanRequired.css';

const PatternLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [styles, setStyles] = useState<Style[]>([]);
  const [skillLevels, setSkillLevels] = useState<SkillLevel[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [styleId, setStyleId] = useState('');
  const [skillLevelId, setSkillLevelId] = useState('');
  const [instructorId, setInstructorId] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([
      publicApi.styles.getAll().then(setStyles).catch(() => {}),
      publicApi.skillLevels.getAll().then(setSkillLevels).catch(() => {}),
      publicApi.instructors.getAll().then(setInstructors).catch(() => {}),
    ]);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    publicApi.patterns
      .getAll({
        styleId: styleId || undefined,
        skillLevelId: skillLevelId || undefined,
        instructorId: instructorId || undefined,
        search: search || undefined,
      })
      .then(setPatterns)
      .catch(() => setError('Failed to load patterns'))
      .finally(() => setLoading(false));
  }, [styleId, skillLevelId, instructorId, search]);

  const clearFilters = () => {
    setStyleId('');
    setSkillLevelId('');
    setInstructorId('');
    setSearch('');
  };

  const resultsLabel = useMemo(() => `${patterns.length} pattern${patterns.length !== 1 ? 's' : ''}`, [patterns.length]);

  return (
    <div className="library-page">
      <div className="pattern-library-header">
        <h1>Patterns</h1>
        <Link to="/library/moves" className="pattern-library-syllabus-link">Browse the syllabus →</Link>
      </div>
      <p>Class combos — ordered sequences of moves, typically 2-3 eight-counts taught together. Pick one and upload your execution to track your progress.</p>

      <div className="filters-section">
        <h2>Filter Patterns</h2>
        <div className="filters">
          <div className="filter-group">
            <label htmlFor="search">Search:</label>
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Title, description, class context…"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="pattern-style-filter">Dance Style:</label>
            <select
              id="pattern-style-filter"
              value={styleId}
              onChange={(e) => setStyleId(e.target.value)}
            >
              <option value="">All Styles</option>
              {styles.map((style) => (
                <option key={style.id} value={style.id}>{style.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="pattern-skill-filter">Skill Level:</label>
            <select
              id="pattern-skill-filter"
              value={skillLevelId}
              onChange={(e) => setSkillLevelId(e.target.value)}
            >
              <option value="">All Levels</option>
              {skillLevels.map((level) => (
                <option key={level.id} value={level.id}>{level.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="pattern-instructor-filter">Instructor:</label>
            <select
              id="pattern-instructor-filter"
              value={instructorId}
              onChange={(e) => setInstructorId(e.target.value)}
            >
              <option value="">All Instructors</option>
              {instructors.map((instructor) => (
                <option key={instructor.id} value={instructor.id}>{instructor.name}</option>
              ))}
            </select>
          </div>

          <button className="btn btn-secondary btn-small clear-filters" onClick={clearFilters}>Clear Filters</button>
        </div>

        <div className="results-count">Showing {resultsLabel}</div>
      </div>

      <div className="moves-section">
        <h2>All Patterns</h2>
        {loading ? (
          <SkeletonGrid />
        ) : error ? (
          <div className="error">{error}</div>
        ) : patterns.length === 0 ? (
          <div className="empty-state">
            <p>No patterns yet. Once an admin creates one, it'll appear here.</p>
          </div>
        ) : (
          <div className="video-grid">
            {patterns.map((pattern) => (
              <div key={pattern.id} className="move-container">
                <div className="video-card">
                  <div className="locked-video">
                    {pattern.accessLevel === 'members' && <span className="locked-video-badge">Members</span>}
                    <VideoThumbnail
                      videoUrl={pattern.videoUrl || ''}
                      thumbnailUrl={pattern.thumbnail || undefined}
                      alt={`${pattern.title} thumbnail`}
                      onClick={() => navigate(`/library/patterns/${pattern.slug || pattern.id}`)}
                    />
                  </div>
                  <div className="move-header">
                    <div className="move-title-container">
                      <h3>
                        <Link to={`/library/patterns/${pattern.slug || pattern.id}`} className="move-title-link">
                          {pattern.title}
                        </Link>
                      </h3>
                    </div>
                  </div>
                  {pattern.classContext && (
                    <p className="pattern-class-context">
                      {pattern.classContext}
                      {pattern.classDate ? ` · ${new Date(pattern.classDate).toLocaleDateString()}` : ''}
                    </p>
                  )}
                  {pattern.description && <p className="description">{pattern.description}</p>}
                  <div className="move-meta">
                    {pattern.style?.name && <span className="style">{pattern.style.name}</span>}
                    {pattern.skillLevel?.name && <span className="level">{pattern.skillLevel.name}</span>}
                    {pattern.instructor?.name && <span className="instructor">by {pattern.instructor.name}</span>}
                  </div>
                  {pattern.moves && pattern.moves.length > 0 && (
                    <div className="pattern-card-moves">
                      {pattern.moves.length} move{pattern.moves.length !== 1 ? 's' : ''}:
                      {' '}
                      {pattern.moves.slice(0, 3).map((pm) => pm.move.title).join(' → ')}
                      {pattern.moves.length > 3 && ` → +${pattern.moves.length - 3} more`}
                    </div>
                  )}
                  <Link
                    to={`/library/patterns/${pattern.slug || pattern.id}`}
                    className="demonstrations-toggle toggle-link"
                  >
                    {pattern._count?.demonstrations != null
                      ? `${pattern._count.demonstrations} demonstration${pattern._count.demonstrations !== 1 ? 's' : ''}`
                      : 'View pattern'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatternLibraryPage;
